import { Checkbox, Form, Modal, Select, SelectItem, SelectSkeleton, Stack, TextArea, TextInput } from '@carbon/react';
import { create, eager, enforce, test } from 'vest';

import useBusinessUnitsQuery from 'shared/api/business-units/useBusinessUnitsQuery';
import useForm from 'utils/hooks/useForm';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import { convertToCurrentSystem, convertToStandardSystem } from 'utils/units';
import styles from 'theme/features/shared/site-specific-spaces.module.scss';

const mapDefaultValues = (space, measurementSystem) =>
  space
    ? {
        name: space.name,
        area: convertToCurrentSystem(space.area, measurementSystem).toString(),
        quantity: space.quantity.toString(),
        notes: space.notes ?? '',
        businessUnit: space.businessUnitId ?? ''
      }
    : {
        name: '',
        area: '',
        quantity: '',
        notes: '',
        businessUnit: ''
      };

const suite = create((data = {}) => {
  eager();

  test('name', 'Space Name is required', () => {
    enforce(data.name).isNotEmpty();
  });

  test('area', 'Area is required', () => {
    enforce(data.area).isNotEmpty();
  });

  test('quantity', 'Quantity is required', () => {
    enforce(data.quantity).isNotEmpty();
  });
});

const SiteSpecificSpaceModal = ({ open, onClose, onSubmit, measurementSystem, siteSpecificSpace = null }) => {
  const { data: businessUnits = [], isLoading: isLoadingBusinessUnits } = useBusinessUnitsQuery();

  const { register, handleSubmit, reset, watch, setValue } = useForm({
    suite,
    defaultValues: mapDefaultValues(siteSpecificSpace, measurementSystem)
  });

  const selectedBusinessUnitId = watch('businessUnit');

  useUpdateEffect(() => {
    if (open) {
      reset(mapDefaultValues(siteSpecificSpace, measurementSystem));
    }
  }, [open, siteSpecificSpace]);

  const handleFormSubmit = (values) => {
    onSubmit({
      name: values.name,
      area: convertToStandardSystem(Number(values.area), measurementSystem),
      quantity: Number(values.quantity),
      notes: values.notes || null,
      businessUnitId: values.businessUnit === '' ? null : Number(values.businessUnit)
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      modalHeading={siteSpecificSpace ? 'Edit the Site-Specific Space' : 'Add a Site-Specific Space'}
      primaryButtonText={siteSpecificSpace ? 'Save' : 'Add to Spaces'}
      secondaryButtonText="Reset"
      onRequestClose={onClose}
      onRequestSubmit={handleSubmit(handleFormSubmit)}
      onSecondarySubmit={() => reset()}
      preventCloseOnClickOutside
    >
      <p className={styles['modal-description']}>
        Site-specific spaces are any special or unique space types that are beyond the typical spread
      </p>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack gap={6}>
          <TextInput id="name" labelText="Space Name" placeholder="Input text" {...register('name')} />
          <Stack orientation="horizontal" gap={7}>
            <TextInput
              id="area"
              labelText="Area"
              placeholder="Enter a number"
              type="number"
              min={0}
              {...register('area')}
            />
            <TextInput
              id="quantity"
              labelText="Quantity"
              placeholder="Enter a number"
              type="number"
              min={0}
              {...register('quantity')}
            />
          </Stack>
          <div className={styles['business-unit']}>
            {isLoadingBusinessUnits ? (
              <SelectSkeleton className={styles['business-unit__input']} />
            ) : (
              <Select
                id="business-unit"
                defaultValue=""
                labelText="Business Unit"
                className={styles['business-unit__input']}
                {...register('businessUnit')}
              >
                <SelectItem value="" text="None" />
                {businessUnits.map((x) => (
                  <SelectItem key={x.id} value={x.id} text={x.name} />
                ))}
              </Select>
            )}
            <Checkbox
              id="non-bu-specific"
              labelText="Non-BU Specific"
              className={styles['business-unit__checkbox']}
              onChange={(_, { checked }) => setValue('businessUnit', checked ? '' : businessUnits[0]?.id ?? '')}
              checked={selectedBusinessUnitId === ''}
            />
          </div>
          <TextArea
            id="notes"
            labelText="Additional notes on this space"
            placeholder="Add notes here"
            maxCount={200}
            {...register('notes')}
          />
        </Stack>
      </Form>
    </Modal>
  );
};

export default SiteSpecificSpaceModal;
